import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Loader } from "semantic-ui-react";
import "semantic-ui-css/semantic.min.css";
import { useLoginCheck } from "../../hooks/useLoginCheck";
import { PREFECTURES } from "../../constants/studioConstants";

// プラン名から1レッスン当たりの返金額を算出
const getRefundAmount = (planName) => {
  if (!planName) return "";
  if (planName.includes("週1")) return 1650;
  if (planName.includes("週2")) return 1540;
  if (planName.includes("週3")) return 1455;
  if (planName.includes("週4")) return 1275;
  if (planName.includes("週5")) return 1275;
  if (planName.includes("週6")) return 1275;
  return "";
};

const DAY_LABELS = {
  monday: "月",
  tuesday: "火",
  wednesday: "水",
  thursday: "木",
  friday: "金",
  saturday: "土",
  sunday: "日",
};

const getCurrentDateValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getMonthValue = (dateStr) => {
  if (!dateStr) return "";
  return dateStr.slice(0, 7);
};

const getDayKey = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return "";
  const date = new Date(year, month - 1, day);
  const dayKeys = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  return dayKeys[date.getDay()];
};

// 選択した休校日にそのレッスンが開講しているか（start_date/end_date が NULL なら無期限）
// 判定は月単位: 開始はその月の1日から、終了はその月の末日までを開講中とみなす
// （例: 終了日が 6/29 でも6月中は対象、開始日が 8/1 なら7月は対象外）
// 日付は "YYYY-MM-DD" 形式なので先頭7文字("YYYY-MM")の文字列比較で月順と一致する
const isLessonActiveOn = (lesson, dateStr) => {
  if (!dateStr) return true;
  const targetMonth = getMonthValue(dateStr);
  if (lesson.start_date && getMonthValue(lesson.start_date) > targetMonth) return false;
  if (lesson.end_date && getMonthValue(lesson.end_date) < targetMonth) return false;
  return true;
};

const formatYenAmount = (amount) => {
  if (typeof amount !== "number") return "-";
  return `¥${amount.toLocaleString("ja-JP")}`;
};

const INVOICE_STATUS_LABELS = {
  draft: "下書き",
  open: "未払い",
  paid: "支払い済み",
  void: "無効",
  uncollectible: "回収不能",
};

const PAYMENT_METHOD_LABELS = {
  card: "カード",
  link: "Link",
  konbini: "コンビニ",
  customer_balance: "銀行振込",
};

const paymentMethodLabel = (pm) => PAYMENT_METHOD_LABELS[pm] || pm || "不明";

// 返金できない場合のメッセージ（詳細取得APIの結果から）
const refundUnavailableMessage = (detail) => {
  if (detail.status !== "paid") {
    const label = INVOICE_STATUS_LABELS[detail.status] || detail.status || "未払い";
    return `この請求書は${label}のため返金できません。`;
  }
  if (!detail.refundable_amount) {
    return "返金可能額がありません（すでに全額返金済みの可能性があります）。";
  }
  if (detail.refund_mode === "manual") {
    return `この支払い方法（${paymentMethodLabel(detail.pm_type)}）は自動返金に対応していません。「請求書」ボタンからStripeで手動返金してください。`;
  }
  if (detail.refund_mode === "email" && !detail.instructions_email) {
    return "返金案内メールの送付先が見つからないため、自動返金できません。「請求書」ボタンからStripeで手動返金してください。";
  }
  return "この請求書は返金できません。";
};

// 返金実行が失敗した場合のメッセージ（実行APIの結果から）
const refundFailureMessage = (result) => {
  switch (result.reason) {
    case "not_found":
      return "対象の請求書が見つかりませんでした。";
    case "not_paid": {
      const label = INVOICE_STATUS_LABELS[result.status] || result.status || "未払い";
      return `未払いのため返金できません（状態: ${label}）。`;
    }
    case "not_refundable":
      return "返金可能な決済が見つかりませんでした。";
    case "manual_required":
      return `この支払い方法（${paymentMethodLabel(result.pm_type)}）は自動返金に対応していません。「請求書」ボタンからStripeで手動返金してください。`;
    case "no_email":
      return "返金案内メールの送付先が見つかりません。「請求書」ボタンからStripeで手動返金してください。";
    case "exceeds_refundable":
      return `返金可能額を超えています（返金可能な残り ${formatYenAmount(result.refundable)}）。`;
    case "amount_mismatch":
      return "返金額の検証に失敗しました（サーバー計算と不一致）。画面を更新して再度お試しください。";
    case "invalid_request":
      return "リクエストが不正です。";
    case "invalid_amount":
    case "invalid_params":
      return "入力値が不正です。";
    case "network":
      return "通信に失敗しました。もう一度お試しください。";
    case "stripe_error":
    case "error":
      return `Stripeでエラーが発生しました。${result.message || ""}`;
    default:
      return "返金に失敗しました。";
  }
};

// 一括返金の行ごとの状態表示
const BULK_STATUS = {
  running: { text: "実行中…", cls: "running" },
  success: { text: "✓ 返金済", cls: "ok" },
  action: { text: "✉ 手続き開始", cls: "act" },
  failed: { text: "✗ 失敗", cls: "ng" },
};

function BulkStatusCell({ state }) {
  if (!state) return <span style={{ color: "#bbb" }}>-</span>;
  const label = BULK_STATUS[state.status];
  if (!label) return <span style={{ color: "#bbb" }}>-</span>;
  return (
    <span className={`bulk-st ${label.cls}`} title={state.message || ""}>
      {label.text}
    </span>
  );
}

function RefundRosterPage() {
  useLoginCheck();

  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedLessonIds, setSelectedLessonIds] = useState([]);
  const [selectedStudios, setSelectedStudios] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedDate, setSelectedDate] = useState(getCurrentDateValue());
  const [copiedRefundAmountKey, setCopiedRefundAmountKey] = useState("");
  const [openingStripeKey, setOpeningStripeKey] = useState("");
  // 作業の目印: 最後にボタンを押した行（Stripe/請求書/返金いずれか）
  const [lastClickedRowKey, setLastClickedRowKey] = useState("");
  // 返金モーダルの状態: null=閉。開いている時は { row, phase, detail, result, error }
  // phase: "loading" | "ready" | "submitting" | "done" | "fetchError"
  const [refundModal, setRefundModal] = useState(null);
  // 一括返金: 選択中の行 / 行ごとの結果 / 実行中のバッチ
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkResults, setBulkResults] = useState({});
  const [bulkRun, setBulkRun] = useState(null);
  const bulkStopRef = useRef(false);
  const bulkRunningRef = useRef(false);
  // 返金実行の二重送信ガード（stateのクロージャに依存せず即時に効く）
  const refundSubmittingRef = useRef(false);
  const copyFeedbackTimerRef = useRef(null);

  useEffect(() => {
    const fetchLessons = async () => {
      setLessonsLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/get_refund_lessons/`, {
          method: "GET",
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error("ネットワークレスポンスが異常です。");
        }
        const data = await response.json();
        setLessons(data);
      } catch (error) {
        console.error("データの取得中にエラーが発生しました:", error);
      } finally {
        setLessonsLoading(false);
      }
    };

    fetchLessons();
  }, []);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimerRef.current) {
        clearTimeout(copyFeedbackTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!openingStripeKey) return;

    const clearOpeningState = () => {
      setOpeningStripeKey("");
    };

    document.addEventListener("pointerdown", clearOpeningState, true);
    return () => {
      document.removeEventListener("pointerdown", clearOpeningState, true);
    };
  }, [openingStripeKey]);

  const dayKey = useMemo(() => getDayKey(selectedDate), [selectedDate]);

  const studioGroups = useMemo(() => {
    const map = new Map();
    const baseLessons = lessons.filter((lesson) => {
      if (dayKey && lesson.day_of_week !== dayKey) return false;
      return isLessonActiveOn(lesson, selectedDate);
    });
    baseLessons.forEach((lesson) => {
      if (!lesson.studio_name) return;
      const prefecture = lesson.studio_prefecture || "その他";
      if (!map.has(prefecture)) {
        map.set(prefecture, new Set());
      }
      map.get(prefecture).add(lesson.studio_name);
    });

    const orderedPrefectures = PREFECTURES.map((pref) => pref.text);
    const result = [];

    orderedPrefectures.forEach((prefecture) => {
      if (map.has(prefecture)) {
        result.push({
          prefecture,
          studios: Array.from(map.get(prefecture)).sort((a, b) =>
            a.localeCompare(b, "ja")
          ),
        });
      }
    });

    Array.from(map.keys())
      .filter((prefecture) => !orderedPrefectures.includes(prefecture))
      .sort((a, b) => a.localeCompare(b, "ja"))
      .forEach((prefecture) => {
        result.push({
          prefecture,
          studios: Array.from(map.get(prefecture)).sort((a, b) =>
            a.localeCompare(b, "ja")
          ),
        });
      });

    return result;
  }, [lessons, dayKey, selectedDate]);

  const filteredLessons = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    return lessons.filter((lesson) => {
      if (dayKey && lesson.day_of_week !== dayKey) return false;
      // 休校日の時点で開講していないレッスン（開始前・終了済み）は候補に出さない
      if (!isLessonActiveOn(lesson, selectedDate)) return false;
      if (selectedStudios.length > 0 && !selectedStudios.includes(lesson.studio_name)) {
        return false;
      }
      if (!keyword) return true;
      const haystack = `${lesson.id} ${lesson.name} ${lesson.studio_name} ${lesson.instructor_name}`.toLowerCase();
      return haystack.includes(keyword);
    });
  }, [lessons, searchKeyword, dayKey, selectedDate, selectedStudios]);

  const selectedLessonSet = useMemo(
    () => new Set(selectedLessonIds),
    [selectedLessonIds]
  );

  const toggleLesson = (lessonId) => {
    setSelectedLessonIds((prev) =>
      prev.includes(lessonId)
        ? prev.filter((id) => id !== lessonId)
        : [...prev, lessonId]
    );
  };

  const selectAllFiltered = () => {
    const filteredIds = filteredLessons.map((lesson) => lesson.id);
    setSelectedLessonIds((prev) => {
      const next = new Set(prev);
      filteredIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const clearSelection = () => {
    setSelectedLessonIds([]);
  };

  const toggleSelectAll = () => {
    if (filteredLessons.length === 0) return;
    const allSelected = filteredLessons.every((lesson) =>
      selectedLessonSet.has(lesson.id)
    );
    if (allSelected) {
      clearSelection();
    } else {
      selectAllFiltered();
    }
  };

  const toggleStudio = (studioName) => {
    setSelectedStudios((prev) =>
      prev.includes(studioName)
        ? prev.filter((name) => name !== studioName)
        : [...prev, studioName]
    );
  };

  useEffect(() => {
    if (selectedLessonIds.length === 0 || !selectedDate) {
      setCustomers([]);
      return;
    }

    const controller = new AbortController();

    const targetMonth = getMonthValue(selectedDate);

    const fetchCustomers = async () => {
      setCustomersLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(
          `${apiUrl}/api/get_refund_customers_from_lessons/`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              lesson_ids: selectedLessonIds,
              target_month: targetMonth,
            }),
            signal: controller.signal,
          }
        );
        if (!response.ok) {
          throw new Error("ネットワークレスポンスが異常です。");
        }
        const data = await response.json();
        setCustomers(data);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("データの取得中にエラーが発生しました:", error);
        }
      } finally {
        setCustomersLoading(false);
      }
    };

    fetchCustomers();

    return () => controller.abort();
  }, [selectedLessonIds, selectedDate]);

  // 休校日やレッスン選択が変わったら、一括返金の選択と結果をクリアする
  useEffect(() => {
    setSelectedRowKeys([]);
    setBulkResults({});
  }, [selectedLessonIds, selectedDate]);

  const rosterRows = useMemo(() => {
    const rows = [];

    customers.forEach((customer) => {
      const studentName = `${customer.last_name} ${customer.first_name}`.trim();
      const planName = customer.plan_name || "";
      const lessons = customer.matched_lessons || [];
      const lessonCount = lessons.length > 0 ? lessons.length : 1;
      const refundAmount = getRefundAmount(planName);
      const studios = new Set();
      const instructors = new Set();

      lessons.forEach((lesson) => {
        if (lesson.studio_name) studios.add(lesson.studio_name);
        if (lesson.instructor_name) instructors.add(lesson.instructor_name);
      });

      rows.push({
        key: `customer-${customer.id}`,
        memberId: customer.id,
        stripeCustomerId: customer.stripe_customer_id,
        studio: Array.from(studios).join(" / ") || customer.studio || "",
        instructor: Array.from(instructors).join(" / "),
        student: studentName,
        plan: planName,
        refundAmount:
          typeof refundAmount === "number" ? refundAmount * lessonCount : "",
        lessonCount,
      });
    });

    return rows.sort((a, b) => {
      const studioCmp = a.studio.localeCompare(b.studio, "ja");
      if (studioCmp !== 0) return studioCmp;

      const instructorCmp = a.instructor.localeCompare(b.instructor, "ja");
      if (instructorCmp !== 0) return instructorCmp;

      const planCmp = a.plan.localeCompare(b.plan, "ja");
      if (planCmp !== 0) return planCmp;

      return a.student.localeCompare(b.student, "ja");
    });
  }, [customers]);

  const handleCopyCsv = async () => {
    if (rosterRows.length === 0) {
      alert("コピー対象がありません。");
      return;
    }

    const formatSelectedDate = () => {
      if (!selectedDate) return "";
      const [year, month, day] = selectedDate.split("-").map(Number);
      if (!year || !month || !day) return "";
      return `${year}/${month}/${day}`;
    };

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      if (/[",\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const selectedDateValue = formatSelectedDate();

    const lines = rosterRows.map((row) =>
      [
        escapeCsv(selectedDateValue),
        escapeCsv(row.studio),
        escapeCsv(row.instructor),
        escapeCsv(row.student),
        escapeCsv(row.plan),
        escapeCsv(row.refundAmount),
        escapeCsv(row.lessonCount > 1 ? `${row.lessonCount}レッスン分` : ""),
      ].join(",")
    );
    const csv = lines.join("\n");

    try {
      await navigator.clipboard.writeText(csv);
      alert("CSVをクリップボードにコピーしました。");
    } catch (error) {
      console.error("コピーに失敗しました:", error);
      alert("コピーに失敗しました。");
    }
  };

  const handleCopyRefundAmount = async (rowKey, amount) => {
    if (typeof amount !== "number") return;

    try {
      await navigator.clipboard.writeText(String(amount));
      setCopiedRefundAmountKey(rowKey);
      if (copyFeedbackTimerRef.current) {
        clearTimeout(copyFeedbackTimerRef.current);
      }
      copyFeedbackTimerRef.current = setTimeout(() => {
        setCopiedRefundAmountKey("");
      }, 1200);
    } catch (error) {
      console.error("金額コピーに失敗しました:", error);
      alert("金額のコピーに失敗しました。");
    }
  };

  // 「Stripe」ボタン: 顧客ページを開く（返金額はクリップボードにコピー）
  const handleOpenCustomer = async (row) => {
    if (!row.stripeCustomerId) return;

    setLastClickedRowKey(row.key);

    if (typeof row.refundAmount === "number") {
      try {
        await navigator.clipboard.writeText(String(row.refundAmount));
      } catch (error) {
        console.error("金額コピーに失敗しました:", error);
      }
    }

    window.open(
      `https://dashboard.stripe.com/customers/${row.stripeCustomerId}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  // 「請求書」ボタン: 選択月の月謝請求書ページを開く（無ければ顧客ページにフォールバック）
  const handleOpenInvoiceWithCopy = async (row) => {
    if (!row.stripeCustomerId) return;

    setLastClickedRowKey(row.key);
    setOpeningStripeKey(row.key);

    // 先に返金額をクリップボードへコピー（この時点ではまだ元タブにフォーカスがある）。
    // ※ window.open で空タブにフォーカスが移った後に writeText を呼ぶと、非フォーカス状態で
    //   固まり後続処理まで止まるため、必ずコピーを先に行う。
    if (typeof row.refundAmount === "number") {
      try {
        await navigator.clipboard.writeText(String(row.refundAmount));
      } catch (error) {
        console.error("金額コピーに失敗しました:", error);
      }
    }

    // ポップアップブロック回避: 空タブを開き、URL確定後に遷移させる
    // （noopener を付けると window 参照が null になり後から遷移できないため、ここでは付けない）
    const stripeWindow = window.open("about:blank", "_blank");

    // 選択月(YYYY-MM)の月謝請求書ページURLを取得。見つからなければ顧客ページにフォールバック
    const fallbackUrl = `https://dashboard.stripe.com/customers/${row.stripeCustomerId}`;
    let targetUrl = fallbackUrl;
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_refund_invoice_url/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          stripe_customer_id: row.stripeCustomerId,
          target_month: getMonthValue(selectedDate),
        }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data && data.url) {
          targetUrl = data.url;
        }
      }
    } catch (error) {
      console.error("請求書URLの取得に失敗しました:", error);
    }

    if (stripeWindow) {
      stripeWindow.location.href = targetUrl;
    } else {
      // ポップアップがブロックされた場合のフォールバック
      window.open(targetUrl, "_blank", "noopener,noreferrer");
    }
  };

  // 返金APIを1件実行する（個別ボタンと一括返金で共通）。実際にお金が動く
  const requestRefund = async (row, targetMonth, closureDate) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const response = await fetch(`${apiUrl}/api/execute_refund_from_invoice/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        stripe_customer_id: row.stripeCustomerId,
        target_month: targetMonth,
        // 休校日。同じ人・同じ月・同じ金額の返金が別の休校日で複数回発生するため、
        // サーバー側の冪等キーに混ぜて握り潰しを防ぐ
        closure_date: closureDate,
        amount: row.refundAmount,
        member_id: row.memberId,
        lesson_count: row.lessonCount,
        plan_name: row.plan,
      }),
    });
    return response.json();
  };

  // 返金モーダルを開き、対象請求書の詳細（返金可否）を取得する
  const openRefundModal = async (row) => {
    setLastClickedRowKey(row.key);
    // モーダルを開いた時点の対象月と休校日を固定（表示中に休校日が変わっても返金対象がブレないように）
    const targetMonth = getMonthValue(selectedDate);
    const closureDate = selectedDate;
    setRefundModal({ row, targetMonth, closureDate, phase: "loading", detail: null, result: null, error: "" });

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_refund_target_invoice/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stripe_customer_id: row.stripeCustomerId,
          target_month: targetMonth,
        }),
      });
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const detail = await response.json();
      setRefundModal((prev) =>
        prev && prev.row.key === row.key
          ? { ...prev, phase: "ready", detail }
          : prev
      );
    } catch (error) {
      console.error("返金対象の取得に失敗しました:", error);
      setRefundModal((prev) =>
        prev && prev.row.key === row.key
          ? { ...prev, phase: "fetchError", error: "請求書情報の取得に失敗しました。" }
          : prev
      );
    }
  };

  const closeRefundModal = () => {
    setRefundModal(null);
  };

  // 返金を実行する（実際にお金が動く）。二重押下は phase で防止
  const submitRefund = async () => {
    // ref による即時ガード（stateの古い値に依存せず、超高速な連打も確実に弾く）
    if (!refundModal || refundSubmittingRef.current) return;
    refundSubmittingRef.current = true;
    const { row, targetMonth, closureDate } = refundModal;

    setRefundModal((prev) => (prev ? { ...prev, phase: "submitting" } : prev));

    try {
      const result = await requestRefund(row, targetMonth, closureDate);
      setRefundModal((prev) => (prev ? { ...prev, phase: "done", result } : prev));
    } catch (error) {
      console.error("返金の実行に失敗しました:", error);
      setRefundModal((prev) =>
        prev
          ? { ...prev, phase: "done", result: { success: false, reason: "network" } }
          : prev
      );
    } finally {
      refundSubmittingRef.current = false;
    }
  };

  // 返金ボタンを出せる行（Stripe顧客IDと金額が揃っている行）
  const refundableRows = useMemo(
    () =>
      rosterRows.filter(
        (row) => row.stripeCustomerId && typeof row.refundAmount === "number"
      ),
    [rosterRows]
  );

  const selectedRowKeySet = useMemo(
    () => new Set(selectedRowKeys),
    [selectedRowKeys]
  );

  const selectedTotal = useMemo(
    () =>
      refundableRows
        .filter((row) => selectedRowKeySet.has(row.key))
        .reduce((sum, row) => sum + row.refundAmount, 0),
    [refundableRows, selectedRowKeySet]
  );

  const toggleRowSelect = (rowKey) => {
    setSelectedRowKeys((prev) =>
      prev.includes(rowKey)
        ? prev.filter((key) => key !== rowKey)
        : [...prev, rowKey]
    );
  };

  const toggleSelectAllRows = () => {
    if (refundableRows.length === 0) return;
    const allSelected = refundableRows.every((row) =>
      selectedRowKeySet.has(row.key)
    );
    setSelectedRowKeys(allSelected ? [] : refundableRows.map((row) => row.key));
  };

  // 一括返金の確認オーバーレイを開く。返金済み/手続き開始済みの行は対象から外す
  const openBulkConfirm = () => {
    const rows = refundableRows.filter((row) => {
      if (!selectedRowKeySet.has(row.key)) return false;
      const state = bulkResults[row.key];
      return !state || (state.status !== "success" && state.status !== "action");
    });
    if (rows.length === 0) {
      alert("返金できる対象が選択されていません（すでに実行済みの可能性があります）。");
      return;
    }
    setBulkRun({
      phase: "confirm",
      rows,
      done: 0,
      stopped: false,
      stopRequested: false,
      // 開いた時点の対象月と休校日を固定（個別返金と同じ考え方）
      targetMonth: getMonthValue(selectedDate),
      closureDate: selectedDate,
    });
  };

  const closeBulkRun = () => {
    if (bulkRunningRef.current) return;   // 実行中は閉じさせない
    setBulkRun(null);
  };

  const requestBulkStop = () => {
    bulkStopRef.current = true;
    setBulkRun((prev) => (prev ? { ...prev, stopRequested: true } : prev));
  };

  // 選択分を1件ずつ順番に返金する。途中で失敗しても止めず、最後に結果をまとめて出す
  const runBulkRefund = async () => {
    if (!bulkRun || bulkRunningRef.current) return;
    bulkRunningRef.current = true;
    bulkStopRef.current = false;
    const { rows, targetMonth, closureDate } = bulkRun;
    setBulkRun((prev) => (prev ? { ...prev, phase: "running", done: 0 } : prev));

    for (let i = 0; i < rows.length; i += 1) {
      if (bulkStopRef.current) break;
      const row = rows[i];
      setBulkResults((prev) => ({ ...prev, [row.key]: { status: "running" } }));
      let state;
      try {
        const result = await requestRefund(row, targetMonth, closureDate);
        if (result && result.success) {
          // コンビニ・銀行振込は顧客が口座を入力するまで着金しない（requires_action）
          const needsAction =
            result.refund_status === "requires_action" ||
            result.refund_status === "pending";
          state = needsAction
            ? {
                status: "action",
                message: `口座案内メール送信: ${result.instructions_email || "-"}`,
              }
            : {
                status: "success",
                message: `${formatYenAmount(result.refunded_amount)} 返金`,
              };
        } else {
          state = { status: "failed", message: refundFailureMessage(result || {}) };
        }
      } catch (error) {
        console.error("一括返金の実行に失敗しました:", error);
        state = {
          status: "failed",
          message: refundFailureMessage({ reason: "network" }),
        };
      }
      setBulkResults((prev) => ({ ...prev, [row.key]: state }));
      setBulkRun((prev) => (prev ? { ...prev, done: i + 1 } : prev));
    }

    const stopped = bulkStopRef.current;
    setBulkRun((prev) => (prev ? { ...prev, phase: "done", stopped } : prev));
    bulkRunningRef.current = false;
  };

  // 一括返金の結果サマリ
  const bulkSummary = useMemo(() => {
    if (!bulkRun) return null;
    const counts = { success: 0, action: 0, failed: 0, untouched: 0 };
    bulkRun.rows.forEach((row) => {
      const state = bulkResults[row.key];
      if (state && counts[state.status] !== undefined) counts[state.status] += 1;
      else counts.untouched += 1;
    });
    return counts;
  }, [bulkRun, bulkResults]);

  return (
    <>
      <section className="lesson-panel">
        <div className="panel-header">
          <h3>休校レッスン選択</h3>
          <span className="count">選択 {selectedLessonIds.length}件</span>
        </div>

        <div className="filters">
          <Input
            fluid
            placeholder="スタジオ / レッスン / 講師で検索"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />

          <div className="date-filter">
            <span className="label">休校日</span>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                clearSelection();
                setSelectedStudios([]);
              }}
            />
            {dayKey && (
              <span className="weekday">
                （{DAY_LABELS[dayKey] || dayKey}）
              </span>
            )}
          </div>

          <div className="studio-filter">
            <span className="label">店舗</span>
            <div className="studio-groups">
              {studioGroups.map((group) => (
                <div className="studio-group" key={group.prefecture}>
                  <div className="studio-title" style={{color: "white"}}>{group.prefecture}</div>
                  <div className="studio-buttons">
                    {group.studios.map((studio) => (
                      <Button
                        key={studio}
                        className={selectedStudios.includes(studio) ? "on" : ""}
                        onClick={() => toggleStudio(studio)}
                      >
                        {studio}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="selection-actions">
            <Button onClick={toggleSelectAll}>
              {filteredLessons.length > 0 &&
              filteredLessons.every((lesson) => selectedLessonSet.has(lesson.id))
                ? "全解除"
                : "表示中を全選択"}
            </Button>
          </div>
        </div>

        <div className="lesson-list">
          <div className="lesson-row lesson-header">
            <div className="cell check"></div>
            <div className="cell" style={{color:"white"}}>スタジオ</div>
            <div className="cell" style={{color:"white"}}>曜日</div>
            <div className="cell" style={{color:"white"}}>時間</div>
            <div className="cell" style={{color:"white"}}>レッスン</div>
            <div className="cell" style={{color:"white"}}>講師</div>
          </div>

          {lessonsLoading && (
            <div className="empty-state">
              <Loader active inline="centered" />
            </div>
          )}

          {!lessonsLoading && filteredLessons.length === 0 && (
            <div className="empty-state" style={{color:"white"}}>該当するレッスンがありません。</div>
          )}

          {!lessonsLoading &&
            filteredLessons.map((lesson) => {
              const isSelected = selectedLessonSet.has(lesson.id);
              const time =
                lesson.start_time && lesson.end_time
                  ? `${lesson.start_time}-${lesson.end_time}`
                  : "";
              return (
                <label
                  key={lesson.id}
                  className={`lesson-row ${isSelected ? "selected" : ""}`}
                >
                  <div className="cell check">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleLesson(lesson.id)}
                    />
                  </div>
                  <div className="cell" style={{color:"white"}}>{lesson.studio_name}</div>
                  <div className="cell" style={{color:"white"}}>
                    {DAY_LABELS[lesson.day_of_week] || lesson.day_of_week}
                  </div>
                  <div className="cell" style={{color:"white"}}>{time}</div>
                  <div className="cell" style={{color:"white"}}>{lesson.name}</div>
                  <div className="cell" style={{color:"white"}}>{lesson.instructor_name}</div>
                </label>
              );
            })}
        </div>
      </section>

      <section className="roster-panel">
        <div className="panel-header">
          <h3>返金対象名簿</h3>
          <div className="panel-actions">
            <span className="count">{rosterRows.length}件</span>
            {selectedRowKeys.length > 0 && (
              <span className="count selected">
                選択 {selectedRowKeys.length}件 / {formatYenAmount(selectedTotal)}
              </span>
            )}
            <Button className="copy-btn" onClick={handleCopyCsv}>
              CSVコピー
            </Button>
            <Button
              className="bulk-refund-btn"
              disabled={selectedRowKeys.length === 0 || !!bulkRun}
              onClick={openBulkConfirm}
            >
              選択分をまとめて返金
            </Button>
          </div>
        </div>

        <div className="roster-list">
          <div className="roster-row roster-header">
            <div className="cell check">
              <input
                type="checkbox"
                checked={
                  refundableRows.length > 0 &&
                  refundableRows.every((row) => selectedRowKeySet.has(row.key))
                }
                onChange={toggleSelectAllRows}
                disabled={refundableRows.length === 0 || !!bulkRun}
                title="表示中を全選択"
              />
            </div>
            <div className="cell" style={{color:"white"}}>会員ID</div>
            <div className="cell" style={{color:"white"}}>スタジオ</div>
            <div className="cell" style={{color:"white"}}>イントラ</div>
            <div className="cell" style={{color:"white"}}>生徒</div>
            <div className="cell" style={{color:"white"}}>プラン</div>
            <div className="cell" style={{color:"white"}}>返金額</div>
            <div className="cell" style={{color:"white"}}>リンク</div>
            <div className="cell" style={{color:"white"}}>返金</div>
            <div className="cell" style={{color:"white"}}>状態</div>
          </div>

          {customersLoading && (
            <div className="empty-state">
              <Loader active inline="centered" />
            </div>
          )}

          {!customersLoading && rosterRows.length === 0 && (
            <div className="empty-state" style={{color:"white"}}>
              {selectedLessonIds.length === 0
                ? "左のレッスンを選択してください。"
                : "対象者がいません。"}
            </div>
          )}

          {!customersLoading &&
            rosterRows.map((row) => (
              <div
                className={`roster-row ${
                  openingStripeKey === row.key ? "is-opening" : ""
                } ${lastClickedRowKey === row.key ? "is-last-clicked" : ""}`}
                key={row.key}
              >
                <div className="cell check">
                  <input
                    type="checkbox"
                    checked={selectedRowKeySet.has(row.key)}
                    onChange={() => toggleRowSelect(row.key)}
                    disabled={
                      !row.stripeCustomerId ||
                      typeof row.refundAmount !== "number" ||
                      !!bulkRun
                    }
                  />
                </div>
                <div className="cell" style={{color:"white"}}>{row.memberId}</div>
                <div className="cell" style={{color:"white"}}>{row.studio}</div>
                <div className="cell" style={{color:"white"}}>{row.instructor}</div>
                <div className="cell" style={{color:"white"}}>{row.student}</div>
                <div className="cell" style={{color:"white"}}>{row.plan}</div>
                <div className="cell">
                  {typeof row.refundAmount === "number" ? (
                    <button
                      type="button"
                      className="refund-amount-btn"
                      title="クリックで金額をコピー"
                      onClick={() =>
                        handleCopyRefundAmount(row.key, row.refundAmount)
                      }
                    >
                      {copiedRefundAmountKey === row.key
                        ? "コピーしました"
                        : formatYenAmount(row.refundAmount)}
                    </button>
                  ) : (
                    <span style={{color:"white"}}>-</span>
                  )}
                </div>
                <div className="cell">
                  {row.stripeCustomerId ? (
                    <div className="link-btns">
                      <button
                        type="button"
                        className="stripe-btn"
                        onClick={() => handleOpenCustomer(row)}
                      >
                        Stripe
                      </button>
                      <button
                        type="button"
                        className={`invoice-btn ${
                          openingStripeKey === row.key ? "is-opening" : ""
                        }`}
                        onClick={() => handleOpenInvoiceWithCopy(row)}
                      >
                        請求書
                      </button>
                    </div>
                  ) : (
                    <span style={{color:"white"}}>-</span>
                  )}
                </div>
                <div className="cell">
                  {row.stripeCustomerId && typeof row.refundAmount === "number" ? (
                    <button
                      type="button"
                      className="refund-btn"
                      onClick={() => openRefundModal(row)}
                      disabled={!!bulkRun}
                    >
                      返金
                    </button>
                  ) : (
                    <span style={{color:"white"}}>-</span>
                  )}
                </div>
                <div className="cell">
                  <BulkStatusCell state={bulkResults[row.key]} />
                </div>
              </div>
            ))}
        </div>
      </section>

      {bulkRun && (
        <div className="refund-modal-backdrop" onClick={closeBulkRun}>
          <div className="refund-modal" onClick={(e) => e.stopPropagation()}>
            <div className="refund-modal-header">
              <h4>まとめて返金</h4>
              {bulkRun.phase !== "running" && (
                <button
                  type="button"
                  className="refund-modal-close"
                  onClick={closeBulkRun}
                  aria-label="閉じる"
                >
                  ×
                </button>
              )}
            </div>

            <div className="refund-modal-body">
              <div className="refund-modal-target">
                <div>
                  <span className="k">休校日</span>
                  <span className="v">{bulkRun.closureDate}</span>
                </div>
                <div>
                  <span className="k">対象月</span>
                  <span className="v">{bulkRun.targetMonth}</span>
                </div>
                <div>
                  <span className="k">件数</span>
                  <span className="v">{bulkRun.rows.length}件</span>
                </div>
                <div>
                  <span className="k">合計返金額</span>
                  <span className="v">
                    {formatYenAmount(
                      bulkRun.rows.reduce((sum, row) => sum + row.refundAmount, 0)
                    )}
                  </span>
                </div>
              </div>

              {bulkRun.phase === "confirm" && (
                <>
                  <p className="refund-modal-msg caution">
                    選択した <strong>{bulkRun.rows.length}件</strong> を上から順番に返金します。
                    <strong>実際にお金が動き、取り消しはできません。</strong>
                    <br />
                    支払いがコンビニ・銀行振込の方には、Stripeから
                    <strong>口座案内メール</strong>が送られ、顧客が口座を入力した時点で着金します
                    （その場では完了しません）。
                  </p>
                  <div className="refund-modal-actions">
                    <button
                      type="button"
                      className="refund-cancel"
                      onClick={closeBulkRun}
                    >
                      キャンセル
                    </button>
                    <button
                      type="button"
                      className="refund-confirm"
                      onClick={runBulkRefund}
                    >
                      {bulkRun.rows.length}件を返金する
                    </button>
                  </div>
                </>
              )}

              {bulkRun.phase === "running" && (
                <>
                  <div className="refund-modal-status">
                    <Loader active inline="centered" size="small" />
                    <p>
                      返金処理中… {bulkRun.done} / {bulkRun.rows.length}件
                    </p>
                  </div>
                  <p className="refund-modal-msg caution">
                    この画面を閉じたりページを移動したりしないでください。
                  </p>
                  <div className="refund-modal-actions">
                    <button
                      type="button"
                      className="refund-cancel"
                      onClick={requestBulkStop}
                      disabled={bulkRun.stopRequested}
                    >
                      {bulkRun.stopRequested
                        ? "中断します（実行中の1件の完了待ち）…"
                        : "中断する（実行中の1件は完了します）"}
                    </button>
                  </div>
                </>
              )}

              {bulkRun.phase === "done" && bulkSummary && (
                <>
                  <p className="refund-modal-msg success">
                    {bulkRun.stopped ? "中断しました。" : "完了しました。"}
                    返金完了 <strong>{bulkSummary.success}件</strong> ／ 手続き開始（口座入力待ち）{" "}
                    <strong>{bulkSummary.action}件</strong> ／ 失敗{" "}
                    <strong>{bulkSummary.failed}件</strong>
                    {bulkSummary.untouched > 0 && (
                      <> ／ 未実行 <strong>{bulkSummary.untouched}件</strong></>
                    )}
                  </p>
                  {(bulkSummary.failed > 0 || bulkSummary.untouched > 0) && (
                    <p className="refund-modal-msg warn">
                      失敗・未実行の行は名簿の「状態」列に理由が出ています。個別の「返金」ボタンから
                      やり直せます。
                    </p>
                  )}
                  <div className="refund-modal-actions">
                    <button
                      type="button"
                      className="refund-confirm"
                      onClick={closeBulkRun}
                    >
                      閉じる
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {refundModal && (
        <div className="refund-modal-backdrop" onClick={closeRefundModal}>
          <div className="refund-modal" onClick={(e) => e.stopPropagation()}>
            <div className="refund-modal-header">
              <h4>返金</h4>
              <button
                type="button"
                className="refund-modal-close"
                onClick={closeRefundModal}
                aria-label="閉じる"
              >
                ×
              </button>
            </div>

            <div className="refund-modal-body">
              <div className="refund-modal-target">
                <div>
                  <span className="k">生徒</span>
                  <span className="v">{refundModal.row.student}</span>
                </div>
                <div>
                  <span className="k">プラン</span>
                  <span className="v">{refundModal.row.plan || "-"}</span>
                </div>
                <div>
                  <span className="k">対象月</span>
                  <span className="v">{refundModal.targetMonth}</span>
                </div>
                <div>
                  <span className="k">返金額</span>
                  <span className="v">{formatYenAmount(refundModal.row.refundAmount)}</span>
                </div>
                {refundModal.detail && refundModal.detail.found && (
                  <div>
                    <span className="k">請求書状態</span>
                    <span className="v">
                      {INVOICE_STATUS_LABELS[refundModal.detail.status] ||
                        refundModal.detail.status ||
                        "-"}
                    </span>
                  </div>
                )}
                {refundModal.detail &&
                  refundModal.detail.found &&
                  refundModal.detail.pm_type && (
                    <div>
                      <span className="k">支払い方法</span>
                      <span className="v">
                        {paymentMethodLabel(refundModal.detail.pm_type)}
                      </span>
                    </div>
                  )}
              </div>

              {refundModal.phase === "loading" && (
                <div className="refund-modal-status">
                  <Loader active inline="centered" size="small" />
                  <p>請求書を確認しています…</p>
                </div>
              )}

              {refundModal.phase === "fetchError" && (
                <p className="refund-modal-msg error">{refundModal.error}</p>
              )}

              {refundModal.phase === "ready" &&
                !refundModal.detail.found && (
                  <p className="refund-modal-msg warn">
                    対象の月謝請求書が見つかりませんでした。返金できません。
                  </p>
                )}

              {refundModal.phase === "ready" &&
                refundModal.detail.found &&
                !refundModal.detail.can_refund && (
                  <p className="refund-modal-msg warn">
                    {refundUnavailableMessage(refundModal.detail)}
                  </p>
                )}

              {refundModal.phase === "ready" &&
                refundModal.detail.found &&
                refundModal.detail.can_refund && (
                  <>
                    {refundModal.detail.refund_mode === "email" ? (
                      <p className="refund-modal-msg caution">
                        支払いは
                        <strong>{paymentMethodLabel(refundModal.detail.pm_type)}</strong>
                        です。返金には顧客の口座情報が必要なため、下記メール宛に
                        <strong>Stripeから口座案内メール</strong>が送られ、顧客が口座を入力すると{" "}
                        <strong>{formatYenAmount(refundModal.row.refundAmount)}</strong>{" "}
                        が返金されます。
                        <br />
                        案内メール送付先: <strong>{refundModal.detail.instructions_email}</strong>
                      </p>
                    ) : (
                      <p className="refund-modal-msg caution">
                        この請求書に{" "}
                        <strong>{formatYenAmount(refundModal.row.refundAmount)}</strong>{" "}
                        を返金します。実際に決済へ払い戻されます。よろしいですか？
                      </p>
                    )}
                    <div className="refund-modal-actions">
                      <button
                        type="button"
                        className="refund-cancel"
                        onClick={closeRefundModal}
                      >
                        キャンセル
                      </button>
                      <button
                        type="button"
                        className="refund-confirm"
                        onClick={submitRefund}
                        disabled={refundModal.phase === "submitting"}
                      >
                        {refundModal.detail.refund_mode === "email"
                          ? "返金手続きを開始する"
                          : "返金する"}
                      </button>
                    </div>
                  </>
                )}

              {refundModal.phase === "submitting" && (
                <div className="refund-modal-status">
                  <Loader active inline="centered" size="small" />
                  <p>返金処理中…</p>
                </div>
              )}

              {refundModal.phase === "done" &&
                refundModal.result &&
                refundModal.result.success && (
                  <>
                    {refundModal.result.refund_status === "requires_action" ? (
                      <p className="refund-modal-msg success">
                        返金手続きを開始しました。顧客（
                        {refundModal.result.instructions_email}）に口座案内メールを送信しました。顧客が口座を入力すると返金が完了します。
                      </p>
                    ) : (
                      <p className="refund-modal-msg success">
                        {formatYenAmount(refundModal.result.refunded_amount)} を返金しました。
                      </p>
                    )}
                    <div className="refund-modal-actions">
                      {refundModal.result.invoice_url && (
                        <a
                          className="refund-open-invoice"
                          href={refundModal.result.invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          請求書を開く
                        </a>
                      )}
                      <button
                        type="button"
                        className="refund-confirm"
                        onClick={closeRefundModal}
                      >
                        閉じる
                      </button>
                    </div>
                  </>
                )}

              {refundModal.phase === "done" &&
                refundModal.result &&
                !refundModal.result.success && (
                  <>
                    <p className="refund-modal-msg error">
                      {refundFailureMessage(refundModal.result)}
                    </p>
                    <div className="refund-modal-actions">
                      <button
                        type="button"
                        className="refund-cancel"
                        onClick={closeRefundModal}
                      >
                        閉じる
                      </button>
                    </div>
                  </>
                )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RefundRosterPage;
